import { CardData, CardDataWithHeroExtras } from 'flows/core/types/cards';
import { ItemKinds } from 'types';
import { DataForCards } from 'flows/core/hooks/use-data-for-cards';
import { EssentialCards } from 'flows/core/types/layouts';
import { Badges, enabledItemDefOfKind, isCompleted, itemOfKind } from './utils';
import { ItemStatuses } from 'constants/statuses';

/* Static Properties */
export const STATIC_LOGOS = [
  {
    url: '/images/util-providers/consolidated-edison.svg',
    alt: 'conEdison',
  },
  {
    url: '/images/util-providers/ng-logo.svg',
    alt: 'National Grid',
  },
  {
    url: '/images/util-providers/nyc-dep-logo.png',
    alt: 'NYC environmental protection',
  },
];

const KIND = ItemKinds.UTILITY;
export const ROB_UTILITY_KINDS = [
  ItemKinds.RESIDENT_ONBOARDING_PROOF_OF_WATER,
  ItemKinds.RESIDENT_ONBOARDING_PROOF_OF_ELECTRIC,
  ItemKinds.RESIDENT_ONBOARDING_PROOF_OF_GAS,
];

const UTILITY_COMMON_STATIC_DATA = {
  subtype: 'UTIL',
  icon: 'LIGHT',
  title: 'Connect utilities',
  action: {
    type: 'internal',
    route: 'utilities',
  },
} as const;

const UTILITY_PROMO_STATIC_DATA = {
  ...UTILITY_COMMON_STATIC_DATA,
  providers: STATIC_LOGOS,
} as const;

export const makeTodayTabUtilityCards = ({
  itemDefinitions,
}: DataForCards, isUPO: boolean): CardDataWithHeroExtras[] => {
  const itemDef = itemDefinitions.find(enabledItemDefOfKind(KIND));

  if (itemDef && !isUPO) {
    return [
      {
        type: 'PROMOTED',
        data: UTILITY_PROMO_STATIC_DATA,
        heroExtras: {
          ctaText: 'Get started',
          heading: 'Check utility providers and get set up on day one.',
        },
        identifier: 'utilities_initiate',
        importanceScore: 250_000,
      },
    ];
  }
  return [];
};

export const makeEssentialsTabUtilityCards = ({
  itemDefinitions,
  items,
  isUPO,
  completedFlows,
  utilitiesConfig,
}: DataForCards & {
  isUPO: boolean;
  completedFlows: string[];
}): EssentialCards => {
  // Arrays to be mutated
  const toDoCards: CardData[] = [];
  const completedCards: CardData[] = [];

  // UPO only shows ROB utilities card
  if(isUPO) return { toDoCards, completedCards };

  const itemDef = itemDefinitions.find(enabledItemDefOfKind(KIND));
  const utilityItems = items.filter(itemOfKind(KIND));
  const utilitiesComplete = completedFlows?.includes('utility');

  const description = 'Review your service providers in one place to set up the essentials.';
  // @ts-expect-error FIXME: TS18048 - strict mode migration
  const required = Boolean(utilitiesConfig.electricRequired || utilitiesConfig.waterRequired || utilitiesConfig.gasRequired);

  if (itemDef) {
    const card: CardData = {
      importanceScore: 250_000,
      type: 'INITIATE',
      data: { ...UTILITY_COMMON_STATIC_DATA, description, required },
      identifier: '',
    };

    if (utilityItems.length === 0) {
      if (utilitiesComplete) {
        card.data.badge = Badges.DONE;
        card.data.status = ItemStatuses.COMPLETED;
        card.data.ctaText = 'View task';
        card.identifier = 'util_initiate_completed';
        completedCards.push(card);
      } else {
        card.identifier = 'util_initiate';
        toDoCards.push(card);
      }
    } else {
      // @ts-expect-error FIXME: TS2345 - strict mode migration
      utilityItems.forEach((utilityItem: { id: string; status: string }) => {
        card.data.status = utilityItem.status;
        if (utilityItem.status && isCompleted(utilityItem)) {
          card.data.ctaText = 'View task';
          card.identifier = `util_initiate_completed_${utilityItem.id}`;
          completedCards.push(card);
        } else {
          card.identifier = `util_initiate_${utilityItem.id}`;
          toDoCards.push(card);
        }
      });
    }
  }

  return {
    toDoCards,
    completedCards,
  };
};
